import { Component, Inject, Input } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FeatureToggle } from '@Enums';
import { UserAdminService, FeatureManagerService } from '@Services';
import { UpdateUserAvailableDto } from '@Models';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { UserFormState } from 'app/enums/global/userprofile-userform-state.enum'
import { UserAvailabilityService } from '@Services/user-availability.service';
import { forkJoin, Observable } from 'rxjs';
import { AppService } from '@App';

@Component({
  selector: 'app-edit-users-v2',
  templateUrl: './edit-users-v2.component.html',
  styleUrls: ['./edit-users-v2.component.scss']
})

export class EditUsersV2Component {
  @Input() selectedUser: any;
  @Input() accounts: any[];
  clients: any[];
  filteredAccounts: any[];
  clientAccountsDictionary: Record<string, any[]>;
  userName: any;
  filterText = '';
  expandAll = false;
  shouldReload = false;
  isUserFormValid = true;
  isUserAvailable = false;
  userFormState: UserFormState = UserFormState.None;
  availability$!: Observable<boolean>

  isDistributionEnabled = false;
  hasAvailabilityToggleViewPermission: any;
  unavailabilityScheduleEnabled: boolean;

  constructor(
    public editUserDialogRef: MatDialogRef<EditUsersV2Component>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly userAdminService: UserAdminService,
    private readonly featureManagerService: FeatureManagerService,
    public availabilityService: UserAvailabilityService,
    private appService: AppService,
  ) {
    this.selectedUser = data.user;
    this.accounts = data.accounts;
  }

  ngOnInit() {
    this.hasAvailabilityToggleViewPermission = this.appService.hasViewPermission('Availability Toggle');
    this.subscribeFeatureFlags();
    this.clients = this.selectedUser?.clients;
    this.filteredAccounts = this.accounts.sort((a, b) => {
      const nameA = a.clientName ?? "";
      const nameB = b.clientName ?? "";
      return nameA.localeCompare(nameB);
    });
    
    this.generateClientAccountsDictionary();
    this.userName = this.selectedUser.firstName + ' ' + this.selectedUser.lastName;
  }

  subscribeFeatureFlags() {
      forkJoin([
          this.featureManagerService.isEnabled(FeatureToggle.GlobalUsersEnableEditDistributionUser),
          this.featureManagerService.isEnabled(FeatureToggle.GlobalEnableUnavailabilitySchedule),
      ]).subscribe(([isDistributionEnabled, unavailabilityScheduleEnabled]) => {
          this.isDistributionEnabled = isDistributionEnabled;
          this.unavailabilityScheduleEnabled = unavailabilityScheduleEnabled;
          if (this.unavailabilityScheduleEnabled && this.hasAvailabilityToggleViewPermission) {
            this.getUserAvailability();
          }
      });
  }

  ngOnDestroy() {
  }

  generateClientAccountsDictionary() {
    const clientAccountsDictionary: Record<string, any[]> = this.filteredAccounts.reduce((dict, account) => {
      if (!dict[account.clientName]) {
        dict[account.clientName] = [];
      }
      dict[account.clientName].push(account);
      return dict;
    }, {} as Record<string, any[]>);

    for (const clientName in clientAccountsDictionary) {
      clientAccountsDictionary[clientName].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    }

    this.clientAccountsDictionary = clientAccountsDictionary;
  }

  filterClients(entry: any) {
    return this.clients.filter(c => c.clientName === entry.key);
  }

  filterClientsAndAccountsByText() {
    this.filteredAccounts = this.accounts.filter(a => a.clientName?.toLowerCase().includes(this.filterText.toLowerCase()) ||
                                              a.code?.includes(this.filterText) ||
                                              a.name?.toLowerCase().includes(this.filterText.toLowerCase()));
    this.generateClientAccountsDictionary();

    if(this.filterText === ''){
      this.expandAll = false;
    }
    else {
      this.expandAll = true;
    }
  }

  reloadUpdatedUser() {
    this.userAdminService.getUser(this.selectedUser.id, true).subscribe((response) => {
      this.clients = response.clients;
    });
  }

  toggleExpandAll() {
    this.expandAll = !this.expandAll;
  }

  onFormValidityChanged(isValid: boolean) {
    this.isUserFormValid = isValid;  
  }

  onFormSaveStateChanged(state: UserFormState) {
    this.userFormState = state;
    if (state === UserFormState.Saved)
    {
      setTimeout(() => {
        this.userFormState = UserFormState.None;
      }, 1500);
    }
    else if (state === UserFormState.Error)
    {
      setTimeout(() => {
        this.userFormState = UserFormState.None;
      }, 4500);
    }
  }
  
  toggleUserAvailability(event: MatSlideToggleChange) {
    const isAvailable = event.checked;
    event.source.checked = !isAvailable;
    this.onFormSaveStateChanged(UserFormState.Saving);
    this.availabilityService.updateAvailability(this.selectedUser.id, event.checked, true).subscribe((response) => {
        if (response) {
          this.onFormSaveStateChanged(UserFormState.Saved);
        } else {
          this.onFormSaveStateChanged(UserFormState.Error);
        }
      });
  }
  
  getUserAvailability() {
      this.availability$ = this.availabilityService.getAvailability(this.selectedUser.id);
  }

  close() {
    this.editUserDialogRef.close(true);
  }
}
